import {
  ArrowRightOutlined,
  EditOutlined,
  EyeOutlined,
  MessageOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  SettingOutlined,
  TeamOutlined,
  UnlockOutlined,
  BankOutlined,
} from '@ant-design/icons';

import {
  Button,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import slugs from '../../resources/slugs';
import { useCms } from '../../store/context/CmsContext';
import { ProfileContext } from '../../store/context/ProfileContext';
import { useContext } from 'react';

const { Title, Text } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const { siteData, loading } = useCms();
  const { profile } = useContext(ProfileContext);
  
  const firstName = profile?.firstName || localStorage.getItem('fullName')?.split(' ')[0] || 'Administrator';

  // Dashboard Pulse Data
  const stats = [
    {
      title: 'Blog Articles',
      value: siteData?.blogs?.length || 0,
      icon: <EditOutlined />,
      color: '#ff4400',
      url: slugs.cms_blogs,
      description: 'Educational articles and news',
    },
    {
      title: 'Active FAQs',
      value:
        (siteData?.faqs?.personal?.length || 0) +
        (siteData?.faqs?.business?.length || 0),
      icon: <QuestionCircleOutlined />,
      color: '#ff4400',
      url: slugs.cms_faqs,
      description: 'Frequently asked questions',
    },
    {
      title: 'Strategic Partners',
      value: siteData?.partners?.length || 0,
      icon: <TeamOutlined />,
      color: '#ff4400',
      url: slugs.cms_partners,
      description: 'Trusted ecosystem partners',
    },
    {
      title: 'User Stories',
      value: siteData?.testimonials?.length || 0,
      icon: <MessageOutlined />,
      color: '#ff4400',
      url: slugs.cms_testimonials,
      description: 'Client success testimonials',
    },
    {
      title: 'Banking Modules',
      value: (siteData?.bankingDetails?.personal?.bullets?.length || 0) + (siteData?.bankingDetails?.business?.bullets?.length || 0),
      icon: <BankOutlined />,
      color: '#ff4400',
      url: slugs.cms_banking,
      description: 'Core banking feature sets',
    },
    {
      title: 'Ecosystem Assets',
      value: siteData?.ecosystem?.length || 0,
      icon: <RocketOutlined />,
      color: '#ff4400',
      url: slugs.cms_ecosystem,
      description: 'Connected app ecosystem',
    },
  ];

  const quickActions = [
    {
      label: 'Edit Hero Section',
      url: slugs.cms_home,
      icon: <PictureOutlined />,
    },
    {
      label: 'Update Site Settings',
      url: slugs.cms_settings,
      icon: <SettingOutlined />,
    },
    {
      label: 'Manage Ecosystem',
      url: slugs.cms_ecosystem,
      icon: <RocketOutlined />,
    },
    {
      label: 'View Live Site',
      url: 'http://localhost:3000',
      icon: <EyeOutlined />,
      external: true,
    },
  ];

  return (
    <div className="waya-dashboard-container" style={{ padding: '0px' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '40px' }}>
        <Space direction="vertical" size={2}>
          <Tag color="#ff4400" style={{ borderRadius: '4px', fontWeight: 600 }}>
            ADMINISTRATION
          </Tag>
          <Title level={2} style={{ margin: '8px 0 4px', fontWeight: 800 }}>
            Welcome back, {firstName}
          </Title>
        </Space>
      </div>

      {/* Pulse Summary Cards */}
      <Row gutter={[24, 24]}>
        {stats.map((item, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              hoverable
              onClick={() => navigate(item.url)}
              bordered={false}
              className="waya-stat-card waya-glass-card"
              style={{
                borderRadius: '24px',
                transition: 'all 0.3s ease',
                height: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    background: `${item.color}15`,
                    color: item.color,
                    padding: '12px',
                    borderRadius: '12px',
                    fontSize: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </div>
                <div style={{ marginLeft: '12px' }}>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {item.title}
                  </Text>
                </div>
              </div>

              <Statistic
                value={item.value}
                loading={loading}
                valueStyle={{
                  color: '#101828',
                  fontWeight: 800,
                  fontSize: '32px',
                }}
              />
              <Text type="secondary" style={{ fontSize: '13px' }}>
                {item.description}
              </Text>

              <div
                style={{
                  marginTop: '16px',
                  borderTop: '1px solid #f0f0f0',
                  paddingTop: '12px',
                }}
              >
                <Text
                  style={{
                    fontSize: '13px',
                    color: item.color,
                    fontWeight: 700,
                  }}
                >
                  Manage Content{' '}
                  <ArrowRightOutlined
                    style={{ fontSize: '10px', marginLeft: '4px' }}
                  />
                </Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Main Feature Panel */}
      <Row gutter={[24, 24]} style={{ marginTop: '32px' }}>
        <Col xs={24} lg={16}>
          <Card
            bordered={false}
            style={{
              borderRadius: '24px',
              background: 'linear-gradient(135deg, #1e1e1e 0%, #a52a0b 100%)',
              color: 'white',
              overflow: 'hidden',
              minHeight: '340px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div style={{ padding: '20px 40px' }}>
              <Title
                level={1}
                className="text-white"
                style={{
                  marginBottom: '16px',
                  fontSize: '36px',
                }}
              >
                Elevate the WayaBank Experience
              </Title>
              <Text
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '18px',
                  display: 'block',
                  marginBottom: '32px',
                  maxWidth: '500px',
                }}
              >
                Quickly edit headlines, replace mockup images, or update site
                descriptions to keep our brand fresh and relevant across all
                channels.
              </Text>
              <Space size="large">
                <Button
                  type="primary"
                  size="large"
                  onClick={() => navigate(slugs.cms_home)}
                  style={{
                    height: '52px',
                    padding: '0 32px',
                    borderRadius: '12px',
                    background: '#ff4400',
                    border: 'none',
                    fontWeight: 700,
                    boxShadow: '0 4px 14px rgba(255, 68, 0, 0.4)',
                  }}
                >
                  Edit Home Page
                </Button>
                <Button
                  ghost
                  size="large"
                  onClick={() => navigate(slugs.cms_settings)}
                  style={{
                    height: '52px',
                    padding: '0 32px',
                    borderRadius: '12px',
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                >
                  Site Settings
                </Button>
              </Space>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={<span style={{ fontWeight: 800 }}>Quick Navigation</span>}
            bordered={false}
            style={{
              borderRadius: '24px',
              height: '100%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {quickActions.map((action, idx) => (
                <div
                  key={idx}
                  onClick={() =>
                    action.external
                      ? window.open(action.url, '_blank')
                      : navigate(action.url)
                  }
                  style={{
                    padding: '16px',
                    borderRadius: '16px',
                    border: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    background: '#fafafa',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#ff4400';
                    e.currentTarget.style.background = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#f0f0f0';
                    e.currentTarget.style.background = '#fafafa';
                  }}
                >
                  <div
                    style={{
                      color: '#ff4400',
                      fontSize: '18px',
                      marginRight: '16px',
                    }}
                  >
                    {action.icon}
                  </div>
                  <Text strong style={{ flex: 1 }}>
                    {action.label}
                  </Text>
                  <ArrowRightOutlined style={{ color: '#bfbfbf' }} />
                </div>
              ))}

              {/* <div
                style={{
                  marginTop: '12px',
                  padding: '20px',
                  borderRadius: '16px',
                  background: '#fdece5',
                  textAlign: 'center',
                }}
              >
                <ShareAltOutlined
                  style={{
                    fontSize: '24px',
                    color: '#ff4400',
                    marginBottom: '8px',
                  }}
                />
                <Title level={5} style={{ margin: 0, color: '#ff4400' }}>
                  Developer Sync
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Synchronized with Isaiah-Dev branch
                </Text>
              </div> */}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
